<template>
  <div class="pt-0 pr-1 pl-1">
    <div class="no-gutters mt-1 mb-2 ml-3 mr-3">
      <div class="row">
        <div class="col-3">
          <div class="parameter-box">
            <h5>{{ expedition_number }}</h5>
            <div class="parameter-name">Cruise number</div>
          </div>
        </div>
        <div class="col-9">
          <div class="parameter-box">
            <h5>{{ expedition_name }}</h5>
            <div class="parameter-name">Cruise description</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-6">
          <div class="parameter-box">
            <h5 v-b-popover.hover.bottom="latitude + ' ' + longitude">{{ position }}</h5>
            <div class="parameter-name">Current position</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="speed_over_ground !== null">
              <a href="#" class="parameter-link" @click="selectParameter(1, 106)" title="Show speed over ground in chart and map">{{ showDigits(speed_over_ground, 1) }}</a> <small>kn</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Speed over ground</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="course_over_ground !== null">
              <a href="#" class="parameter-link" @click="selectParameter(1, 100)" title="Show course over ground in chart and map">{{ showDigits(course_over_ground, 1) }}</a><small>°</small>
              <font-awesome-icon :icon="['fas', 'location-arrow']" size="xs" class="text-info ml-2" :style="{ transform: rotateArrow(course_over_ground) }" />
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Course over ground</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="air_temperature !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 1)" title="Show air temperature in chart and map">{{ showDigits(air_temperature, 1) }}</a> <small>°C</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Air temperature</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="air_pressure !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 2)" title="Show air pressure in chart and map">{{ showDigits(air_pressure, 0) }}</a> <small>hPa</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Air pressure</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="water_temperature !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 1)" title="Show water temperature in chart and map">{{ showDigits(water_temperature, 1) }}</a> <small>°C</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Water temperature</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="salinity !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 2)" title="Show salinity in chart and map">{{ showDigits(salinity, 1) }}</a> <small>PSU</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Salinity</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import Helpers from '../../../mixins/helpers'

export default {
  name: 'ShipDShipDashboardWidget',
  mixins: [
    Helpers
  ],
  data: function () {
    return {}
  },
  props: ['platformId'],
  methods: {
    ...mapMutations(['select_parameter']),
    rotateArrow: function (heading) {
      return 'rotate(' + Number(Number(heading) - 45) + 'deg)'
    },
    selectParameter: function (entityId, parameterId) {
      this.select_parameter([this.platformId, entityId, parameterId])
    }
  },
  computed: {
    ...mapState(['platforms']),
    ...mapGetters(['currentUpdate', 'lastUpdate']),
    position () {
      const position = this.getCoordinatesNautical(this.roundDigits(this.latitude, 5),
        this.roundDigits(this.longitude, 5))
      return position.lat + ' ' + position.lng
    },
    latitude () {
      const update = this.currentUpdate(this.platformId, 1, 1)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    longitude () {
      const update = this.currentUpdate(this.platformId, 1, 2)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    heading () {
      const update = this.currentUpdate(this.platformId, 1, 3)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    course_over_ground () {
      const update = this.currentUpdate(this.platformId, 1, 100)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    speed_over_ground () {
      const update = this.currentUpdate(this.platformId, 1, 106)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    air_temperature () {
      const update = this.currentUpdate(this.platformId, 101, 1)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    air_pressure () {
      const update = this.currentUpdate(this.platformId, 101, 2)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    water_temperature () {
      const update = this.currentUpdate(this.platformId, 102, 1)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    salinity () {
      const update = this.currentUpdate(this.platformId, 102, 2)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    expedition_name () {
      const update = this.currentUpdate(this.platformId, 3, 1)
      return (update.length !== 0) ? update[1] : '- // -'
    },
    expedition_number () {
      const update = this.currentUpdate(this.platformId, 3, 2)
      return (update.length !== 0) ? update[1] : '---'
    }
  }
}
</script>

<style lang="scss" scoped>
  .parameter-box {
    padding: 10px 10px 5px 10px;
  }
  .parameter-box h4 {
    margin-bottom: 2px;
  }
  .parameter-box h5 {
    margin-bottom: 2px;
  }
  .parameter-name {
    font-size: 11px;
    color: #6c757d;
  }
  .parameter-link {
    color: #212529;
    text-decoration: none;
  }
  .state-box {
    font-size: 12px;
    font-weight: bold;
    text-align: center;
    vertical-align: bottom;
    border-radius: 2px;
    padding: 5px 2px 5px 2px;
  }
  .state-ok {
    background-color: #82E0AA;
    color: #186A3B;
  }
  .state-danger {
    background-color: #F1948A;
    color: #78281F;
  }
  .no-gutters {
    margin-right: 10px;
    margin-left: 10px;
    .col,
    [class*="col-"] {
      padding-right: 2px;
      padding-left: 2px;
    }
  }
</style>
